import { fireEventReady } from './events'
import { mapPage } from './pageMaps'
import { getReferrerTrackingId } from './trackingMaps'
import { createGuid } from './util'
import { getEnvironment } from '../environment'
import CookieHelper from '../utils/CookieHelper'
import { BrowserUtils } from '../utils/siteUtils'
import { getAkamaiZipCode } from '../utils/getAkamaiZipCode/getAkamaiZipCode'

const getDataLayer = location => {
  if (BrowserUtils.isBrowser) {
    if (!window.ICO || !window.ICO.dataLayer) {
      const environment = getEnvironment()

      window.s_account = environment.kbbOmnitureSuite
      window.globalOrd = (Math.random() * 10000000000000000).toFixed(0)

      window.ICO = {}
      window.ICO.dataLayer = {}
      window.ICO.dataLayer.cookies = {}
      window.ICO.dataLayer.device = {}
      window.ICO.dataLayer.impressions = []
      window.ICO.dataLayer.location = {}
      window.ICO.dataLayer.offer = {}
      window.ICO.dataLayer.pageInfo = {}
      window.ICO.dataLayer.replacementVehicle = {}
      window.ICO.dataLayer.session = getSessionData(location)
      window.ICO.dataLayer.vehicle = {}
      window.ICO.dataLayer.contactInfo = {}

      console.log('Successfully created ICO data layer.')
    }

    return window.ICO.dataLayer
  }
}

const getSessionData = location => {
  const sessionId = CookieHelper.get('SessionId') || createGuid()
  const visitorId = CookieHelper.get('VisitorId') || createGuid()
  const trackingId = getReferrerTrackingId(location, document.referrer)

  if (sessionId) CookieHelper.set('SessionId', sessionId)
  if (visitorId) CookieHelper.set('VisitorId', visitorId, 30)

  return { sessionId, visitorId, trackingId, referrer: document.referrer, url: window.location.href }
}

const getPrefix = experienceState => {
  const { isAdmin, experience } = experienceState
  return !isAdmin ? (typeof experience !== 'undefined' && experience !== '' ? experience : 'kbb') : 'dat'
}

// See dataLayer analytics mapping at end of file
const setDataLayer = ({ landingContext, page, event, location }) => {
  const { experienceState, getQuestionResponse, getQuestionResponseComment, trim, year, make, model, transmission, bodyStyle, drivetrain, color, offer, globalState, urlState, variation, isEdit, mileage, updateGlobalState } = landingContext
  const { isMazdaDealer, offerCode, dealerId, isDigitalRetail, isAssigned, isStellantisDealer, oemDealerCode, isToyotaDealer, isLexusDealer, isGMDealer, measurementId, isGenesisDealer, cobrandingInfo, offerId } = experienceState
  const dataLayer = getDataLayer(location)

  if (BrowserUtils.isBrowser && dataLayer) {
    const prefix = getPrefix(experienceState)

    dataLayer.impressions = []
    dataLayer.offer = {}
    dataLayer.replacementVehicle = {}
    dataLayer.cookies = setCookies(location)
    const oldPageInfo = dataLayer.pageInfo || {}
    const newPageInfo = mapPage(page) || {}
    dataLayer.pageInfo = { ...oldPageInfo, ...newPageInfo }
    dataLayer.pageInfo.page = page
    dataLayer.pageInfo.path = `${prefix}_${isEdit ? 'edit' : 'create'}`
    dataLayer.pageInfo.experience = prefix
    dataLayer.pageInfo.partnerDescription = ''
    dataLayer.pageInfo.challengerId = ''
    dataLayer.pageInfo.productString = ''
    dataLayer.pageInfo.qs = location && location.initQueryParams ? location.initQueryParams : {}
    dataLayer.uploadedPhoto = globalState.uploadedPhoto
    dataLayer.pageInfo.experimentId = globalState.optimizelyVariationId
    dataLayer.measurementId = measurementId || cobrandingInfo?.measurementID
    if (!!experienceState.sessionId) {
      dataLayer.session.sessionId = experienceState.sessionId
      dataLayer.session.continueTradeIn = true
    }

    if (variation !== undefined) {
      dataLayer.pageInfo.challengerId = 'us341750-' + variation
    }

    if (dataLayer.pageInfo.linkName && dataLayer.pageInfo.linkName.indexOf('vinscan') > 0) {
      dataLayer.pageInfo.partnerDescription = `offers-${dataLayer.pageInfo.linkName.substring(dataLayer.pageInfo.linkName.indexOf('vinscan')).replace('_', '-')}`

      // vinscan_open should be a view, not a link
      if (dataLayer.pageInfo.linkName.indexOf('open') > 0) {
        event = 'PageReady'
      }
    }

    dataLayer.device.isMobile = BrowserUtils.isMobile()

    dataLayer.location.zip = globalState.zip
    dataLayer.location.state = globalState.state

    dataLayer.offer.offerCode = offerCode
    dataLayer.offer.dealerId = dealerId
    dataLayer.offer.isAssigned = isAssigned
    dataLayer.offer.isDigitalRetail = isDigitalRetail
    dataLayer.offer.isStellantisDealer = isStellantisDealer
    dataLayer.offer.isMazdaDealer = isMazdaDealer
    dataLayer.offer.oemDealerCode = oemDealerCode
    dataLayer.offer.isToyotaDealer = isToyotaDealer
    dataLayer.offer.isLexusDealer = isLexusDealer
    dataLayer.offer.isGMDealer = isGMDealer
    dataLayer.offer.isGenesisDealer = isGenesisDealer

    if (offerId) {
      dataLayer.offer.offerId = offerId
    }
    if (offer) {
      if (offer.prospect) {
        dataLayer.offer.channelId = offer.prospect.channelId
      }

      dataLayer.offer.offerId = offer.offerId
      dataLayer.offer.offerValue = offer.amount

      if (offer.chosenCondition === 'Poor') {
        dataLayer.offer.offerState = 'offer_pc'
      } else if (offer.status === 'INELIGIBLE') {
        dataLayer.offer.offerState = 'offer_ne'
      } else if (offer.stats === 'FR') {
        dataLayer.offer.offerState = 'offer_pr'
      } else if (!isTradeInWithinRange(offer)) {
        dataLayer.offer.offerState = 'offer_oor'
      } else {
        dataLayer.offer.offerState = 'offer'
      }
    }

    if (getQuestionResponse('qrv/LeadType').v) {
      dataLayer.offer.optIn = getQuestionResponse('qrv/LeadType').v === 'OptIn' ? 'trade' : 'sell'

      const lfnQ = getQuestionResponse('qrv/LookingForNewCars').v === 0
      const lfcQ = getQuestionResponse('qrv/LookingForCertifiedCars').v === 0
      const lfuQ = getQuestionResponse('qrv/LookingForUsedCars').v === 0

      if (lfnQ || lfcQ || lfuQ) {
        const allocationEntries = offer?.prospect?.dealershipsAndInventory?.allocationEntries || []
        const { voiMake, voiModel } = allocationEntries[0]?.dealerInfo || {}

        const yrValue = getQuestionResponseComment('qrv/YearRange') && getQuestionResponseComment('qrv/YearRange').split(',')
        const makeName = getQuestionResponseComment('qrv/Make') || voiMake
        const modelName = getQuestionResponseComment('qrv/Model') || voiModel
        const makemodel = offer && offer.prospect && offer.prospect.replacementVehicle && offer.prospect.replacementVehicle.find(q => q.questionId === 'qrv/MakeAndModel')
        const makemodel1 = makemodel && makemodel.subquestions.find(i => i.questionId === 'qrv/MakeModel1')
        dataLayer.replacementVehicle.types = []

        lfnQ && dataLayer.replacementVehicle.types.push('new')
        lfcQ && dataLayer.replacementVehicle.types.push('cpo')
        lfuQ && dataLayer.replacementVehicle.types.push('used')

        dataLayer.replacementVehicle.minYear = yrValue ? (yrValue[0] <= yrValue[1] ? yrValue[0] : yrValue[1]) : ''
        dataLayer.replacementVehicle.maxYear = yrValue ? (yrValue[1] >= yrValue[0] ? yrValue[1] : yrValue[0]) : ''
        dataLayer.replacementVehicle.make = makeName || ''
        dataLayer.replacementVehicle.model = modelName || ''
        dataLayer.replacementVehicle.makeId = makemodel1?.value && makemodel1.value.split('/')[1]
        dataLayer.replacementVehicle.modelId = makemodel1?.value && makemodel1.value.split('/')[2]
      }
    }
    dataLayer.vehicle.vin = globalState.vin || urlState.vin || (offer && offer.prospect.vehicle.vin)
    dataLayer.vehicle.id = trim ? trim.trimId : 0
    dataLayer.vehicle.trimName = trim ? trim.displayName : ''
    dataLayer.vehicle.yearId = year ? year.yearId : 0
    dataLayer.vehicle.makeId = make ? make.makeId : 0
    dataLayer.vehicle.makeName = make ? make.displayName : ''
    dataLayer.vehicle.modelId = model ? model.modelId : 0
    dataLayer.vehicle.modelName = model ? model.displayName : ''
    dataLayer.vehicle.bodyStyle = bodyStyle || ''
    dataLayer.vehicle.transmission = transmission ? transmission.displayName : ''
    dataLayer.vehicle.drivetrain = drivetrain ? drivetrain.displayName : ''
    dataLayer.vehicle.color = color ? color.displayName : ''
    dataLayer.vehicle.trimId = offer && offer.prospect.vehicle.trimId
    dataLayer.vehicle.chosenCondition = offer && offer.chosenCondition
    dataLayer.vehicle.mileage = mileage
    dataLayer.vehicle.price = offer && offer.amount
    dataLayer.vehicle.entry = globalState.entry === 'mm' ? 'ymm' : globalState.entry || ''

    dataLayer.userZipcode = getAkamaiZipCode()

    if (location.pathname.includes('sidebyside')) {
      return
    }

    if (typeof event !== 'undefined' && event !== '') {
      fireEventReady(page, event, dataLayer, globalState, updateGlobalState, landingContext)
    }
  }
}

const addLinkNameToDataLayer = tag => {
  const dataLayer = getDataLayer()

  if (dataLayer && dataLayer.pageInfo && typeof tag !== 'undefined' && tag !== '') {
    dataLayer.pageInfo.linkName = tag
  }
}

const appendLinkNameInDataLayer = addTag => {
  const dataLayer = getDataLayer()

  if (dataLayer && dataLayer.pageInfo && typeof dataLayer.pageInfo.linkName !== 'undefined' && dataLayer.pageInfo.linkName !== '' && dataLayer.pageInfo.linkName.indexOf(addTag) < 0) {
    dataLayer.pageInfo.linkName += addTag
  }
}

const appendContactInfo = globalState => {
  const dataLayer = getDataLayer()

  if (dataLayer) {
    dataLayer.consumerContactInfo = {
      firstName: globalState?.firstName,
      lastName: globalState?.lastName,
      email: globalState?.email,
      zip: globalState?.zip,
      phone: globalState?.phone
    }
  }
}

const appendOfferInfo = globalState => {
  const dataLayer = getDataLayer()
  const { dealer } = globalState?.certificateInfo || {}

  if (dataLayer && dealer) {
    const { dealerIndex, dealerPhone } = dealer || {}

    dataLayer.certificateInfo = {
      dealer: {
        order: dealerIndex || dealerIndex === 0 ? dealerIndex + 1 : undefined,
        dealerPhone: dealerPhone
      }
    }
  }
}

// Send an array to replace impression list, send string to add to existing list
const addImpressionsToDataLayer = tags => {
  const dataLayer = getDataLayer()

  if (typeof tags !== 'undefined') {
    if (Array.isArray(tags)) {
      dataLayer.impressionTags = tags
    } else {
      if (dataLayer.impressionTags && !dataLayer.impressionTags.find(tag => tag === tags)) {
        dataLayer.impressionTags.push({ key: tags })
      } else dataLayer.impressionTags = [{ key: tags }]
    }
  } else {
    dataLayer.impressionTags = []
  }
}

const isTradeInWithinRange = offerdata => {
  if (offerdata && offerdata.valuation && offerdata.valuation[0].valuationPrices && offerdata.status === 'CLEARED' && offerdata.chosenCondition && offerdata.chosenCondition !== 'Poor') {
    const rangeHigh = offerdata.valuation[0].valuationPrices.find(price => price.priceType === `TradeIn${offerdata.chosenCondition}RangeHigh`)
    const rangeLow = offerdata.valuation[0].valuationPrices.find(price => price.priceType === `TradeIn${offerdata.chosenCondition}RangeLow`)

    if (rangeHigh && rangeHigh.value && rangeLow && rangeLow.value) return rangeLow.value > offerdata.amount && rangeHigh.value > offerdata.amount
  }
  return false
}

const setCookies = location => {
  const qs = location && location.initQueryParams ? location.initQueryParams : {}
  const lp = (qs && qs.lp) || ''
  const lnx = (qs && qs.lnx) || CookieHelper.get('lnx') || ''
  const psid = (qs && qs.psid) || CookieHelper.get('psid') || ''
  const siomid = (qs && qs.siomid) || CookieHelper.get('siomid') || ''
  const pxa_realid = CookieHelper.get('pxa_realid') || ''
  let pxa_id = CookieHelper.get('abc') || CookieHelper.get('pxa_id') || ''

  pxa_id = pxa_id.match(/[^A-Z0-9]/gi) ? '' : pxa_id

  if (lnx) CookieHelper.set('lnx', lnx, 0, 30)
  if (psid) CookieHelper.set('psid', psid, 0, 30)
  if (siomid) CookieHelper.set('siomid', siomid, 0, 30)

  return { lp, lnx, psid, siomid, pxa_id, pxa_realid }
}

export { getDataLayer, setDataLayer, addLinkNameToDataLayer, appendLinkNameInDataLayer, addImpressionsToDataLayer, appendContactInfo, appendOfferInfo }

/* 
DataLayer -> Analytics mapping 

    dataLayer.pageInfo
      .pageName - s.pageName, pixall.pageName, gTag.u6, cs.kbb.pageName, cs.atc.page
      .sectionName - s.channel, pixall.omnitureSectionName, gTag.u4, cs.kbb.channel, cs.atc.sectkon
      .superSectionName - s.prop1, pixall.omnitureSuperSection, gTag.u5, cs.kbb.superSection, cs.atc.pageName
      .detailPageName - s.prop2, pixall.params, omnitureDetailedPageName, cs.kbb.detPageName, cs.atc.detailedPageName
      .friendlyPageName - GA page name
      .linkName - s.linkName, s.products, s.prop18
      .productString - s.products
      .challengerId - s.prop39
      .partnerDescription - s.prop18
      .path - s.eVar48
      .pageType - pixall.pageType
      .page
      .qs

    dataLayer.offer
      .channelId - s.eVar8
      .offerCode - s.prop43, _mTrack.category
      .offerId - s.eVar7, pixall.formEventId, dcm.link, _mtrack.orderId
      .offerValue - s.prop57, pixall.price, gTag.u3, dcm.link
      .offerState - gTag.u1
      .optIn - s.eVar50, _mTrack.product
      .dealerId - s.eVar41
      .isAssigned
      .isDigitalRetail
      .amount

    dataLayer.location
      .zip - s.zip, s.prop12, pixall.searchFacetZipCode, gTag.u2
      .state - s.state, s.prop13
    
    dataLayer.vehicle
      .id - s.prop28, gTag.u8
      .trimName - gmc.gTag.u5
      .vin
      .yearId - s.prop4, pixall.year, gTag.u11, gmc.gTag.u3
      .makeId - s.prop26
      .makeName - s.prop5, pixall.make, gTag.u12, gmc.gTag.u4, cs.kbb.make, cs.atc.vehicleMake
      .modelId - s.prop27
      .modelName - pixall.model, gTag.u13, gmc.gTag.u4, cs.kbb.model, cs.atc.vehicleModel
      .bodyStyle - s.prop58, pixall.bodyStyle, gTag.u9, cs.atc.carType
      .transmission
      .drivetrain
      .color
      .intent - pixall.vehicleType, cs.kbb.intent, cs.atc.listingPriorityType
    
    dataLayer.replacementVehicle
      .types - pixall.searchFacetVehicleTypes, cs.atc.searchType
      .minYear - pixall.searchFacetMinYear, cs.atc.search_min_year
      .maxYear - pixall.searchFacetMaxYear, cs.atc.search_max_year
      .make - pixall.searchFacetMake
      .model - pixall.searchFacetModel

    dataLayer.cookies
      .lp - s.prop42
      .lnx - s.eVar81
      .psid - s.prop31, s.evar70
      .pxa_id - s.eVar90
      .pxa_realid - s.eVar93
      .siomid - s.prop64, s.evar5

    dataLayer.session
      .visitorId
      .sessionId - s.prop41, dcm.link, 360session
      .trackingId - s.prop21, cs.kbb.searchEngine
      .pageInstance - s.prop17
      .referrer
      .url

    dataLayer.device
      .isMobile

    dataLayer
      .impressions - s.products
      .impressionTags
      .productString
*/
